import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CompaniesService } from '@services/companies.service';
import { CompanyModel } from '@data-layer/models/companies';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { ApplicationModeService } from '@services/application-mode.service';
import { enumApplicationMode } from '@enum/enumApplicationMode';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { Observable, ReplaySubject, forkJoin, of } from 'rxjs';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import { BatchImportModel, PaginatedResultModel } from '@data-layer/models/common';
import { enumBatchImportItemType, enumBatchImportStatus } from '@data-layer/enums/common';
import { BatchImportService } from '@shared/batch-import-mutual/services';
import { BillModel, PaymentMethodModel } from '@data-layer/models/payments';
import { enumPaymentProvider, enumPaymentState } from '@data-layer/enums/payments';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { OrganizationsService } from '@services/organizations.service';
import { OrganizationMemberModel, OrganizationModel } from '@app/data-layer/models/organizations';
import { BillsRepository, PaymentsRepository } from '@app/data-layer/repositories';
import { UserBetaFlagModel, UserModel } from '@app/data-layer/models/user';
import { OrganizationsRepository } from '@app/data-layer/repositories/organizations.repository';
import { enumOrganizationRole } from '@app/data-layer/enums/organizations';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { TokenService } from '@services/token.service';

@Component({
  selector: 'app-user-warnings',
  templateUrl: './user-warnings.component.html',
  styleUrls: ['./user-warnings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserWarningsComponent implements OnInit, OnDestroy {
  public organization: OrganizationModel;
  public company: CompanyModel;
  public firstCompany: CompanyModel;
  public currentUser: UserModel;
  public DEFAULT_CURRENCY_CODE: typeof DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;
  public isShowBatchReview: boolean;
  public isShowMonthlyFailed: boolean;
  public isInLclQuote: boolean;
  public isInLclBooking: boolean;
  public isInLclShipment: boolean;
  public allBatchItems: BatchImportModel[] = [];
  public filteredBatchItems: BatchImportModel[] = [];
  public organizationFailedBill: BillModel;
  public companyFailedBill: BillModel;
  public currentUserMember: OrganizationMemberModel;
  public paymentMethods: PaymentMethodModel[] = [];
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private router: Router,
    private organizationsRepository: OrganizationsRepository,
    private organizationsService: OrganizationsService,
    private companiesService: CompaniesService,
    private applicationModeService: ApplicationModeService,
    private paymentsRepository: PaymentsRepository,
    private billsRepository: BillsRepository,
    private cdr: ChangeDetectorRef,
    private tokenService: TokenService,
    private batchImportService: BatchImportService,
  ) {}

  public ngOnInit(): void {
    this.company = this.companiesService.getCompany();
    this.calcClasses();
    this.loadPaymentMethods();

    this.currentUser = this.companiesService.getUser();
    this.firstCompany = this.companiesService.getCompanies()[0];

    this.organizationsService
      .getOrganizationByIdFromServer(this.currentUser.defaultOrganizationId || this.firstCompany.organizationId)
      .pipe(
        switchMap((organization) => {
          return forkJoin(of(organization), this.organizationsRepository.getOrganizationMembers(organization.id));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(([organization, organizationMembers]) => {
        this.organization = organization;
        this.currentUserMember = organizationMembers?.find((member) => member.user.userId === this.currentUser.uid);
        if (this.applicationModeService.activeApplicationMode === enumApplicationMode.SUPPORT) {
          this.organizationsService.organizationFailedBill$.next(null);
        } else if (this.applicationModeService.activeApplicationMode === enumApplicationMode.USER) {
          this.getOrganizationFailedBill();
        }
        this.calcClasses();

        this.cdr.markForCheck();
      });

    this.companiesService.companyChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe((changeCompany: { previous: CompanyModel; current: CompanyModel }) => {
        if (!!changeCompany.current && this.applicationModeService.activeApplicationMode === enumApplicationMode.USER) {
          this.company = changeCompany.current;
          this.getCompanyFailedBill();
          this.calcClasses();

          this.cdr.markForCheck();
        }
      });

    this.router.events
      .pipe(
        filter((event: RouterEvent) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        if (this.tokenService.isAuthenticated && this.applicationModeService.activeApplicationMode === enumApplicationMode.USER) {
          this.company = this.companiesService.getCompany();
          this.calcClasses();

          this.cdr.markForCheck();
        }

        if (this.applicationModeService.activeApplicationMode === enumApplicationMode.SUPPORT) {
          this.organizationsService.organizationFailedBill$.next(null);
          this.companiesService.companyFailedBill$.next(null);
        }
      });

    this.batchImportService.getBatchImports$.pipe(takeUntil(this.destroy$)).subscribe((batchItems: BatchImportModel[]) => {
      this.allBatchItems = batchItems;
      this.isShowBatchReview = this.calcIsShowBatchReview;
      this.cdr.markForCheck();
    });

    this.organizationsService.organizationFailedBill$.pipe(takeUntil(this.destroy$)).subscribe((bill: BillModel) => {
      this.organizationFailedBill = bill;
      this.calcClasses();

      this.cdr.markForCheck();
    });

    this.companiesService.companyFailedBill$.pipe(takeUntil(this.destroy$)).subscribe((bill: BillModel) => {
      this.companyFailedBill = bill;
      this.calcClasses();

      this.cdr.markForCheck();
    });

    if (this.applicationModeService.activeApplicationMode === enumApplicationMode.SUPPORT) {
      this.companiesService.companyFailedBill$.next(null);
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get calcIsShowBatchReview(): boolean {
    this.filteredBatchItems = [];

    if (!this.company || this.applicationModeService.activeApplicationMode !== enumApplicationMode.USER) {
      return;
    }

    if (location.pathname.endsWith('/isf') && this.isfBatchItems.length) {
      this.filteredBatchItems.push(this.isfBatchItems[0]);
    }

    if (location.pathname.endsWith('/entry-summary') && this.entrySummaryBatchItems.length) {
      this.filteredBatchItems.push(this.entrySummaryBatchItems[0]);
    }

    if (location.pathname.endsWith('/dashboard') && (this.isfBatchItems.length || this.entrySummaryBatchItems.length)) {
      if (this.isfBatchItems.length) {
        this.filteredBatchItems.push(this.isfBatchItems[0]);
      }
      if (this.entrySummaryBatchItems.length) {
        this.filteredBatchItems.push(this.entrySummaryBatchItems[0]);
      }
    }

    return !!this.filteredBatchItems.length;
  }

  public get isOrganizationMember(): boolean {
    return this.currentUserMember?.role === enumOrganizationRole.CONTRIBUTER;
  }

  public get calcIsInLclQuote(): boolean {
    return location.pathname.includes('lcl-shipping') && location.pathname.endsWith('/edit');
  }

  public get calcIsInLclBooking(): boolean {
    return location.pathname.includes('lcl-shipping') && location.pathname.endsWith('/booking');
  }

  public get calcIsInLclShipment(): boolean {
    return location.pathname.includes('lcl-shipping') && location.pathname.endsWith('/shipment');
  }

  public get isfBatchItems(): BatchImportModel[] {
    return (this.allBatchItems || []).filter(
      (item: BatchImportModel) =>
        item.itemType === enumBatchImportItemType.ISF &&
        [enumBatchImportStatus.PARTIALLY_FINISHED, enumBatchImportStatus.FINISHED].includes(item.status),
    );
  }

  public get entrySummaryBatchItems(): BatchImportModel[] {
    return (this.allBatchItems || []).filter(
      (item: BatchImportModel) =>
        item.itemType === enumBatchImportItemType.ENTRY_SUMMARY &&
        [enumBatchImportStatus.PARTIALLY_FINISHED, enumBatchImportStatus.FINISHED].includes(item.status),
    );
  }

  public get calcIsShowMonthlyFailed(): boolean {
    if (!this.organization && !this.company) {
      return;
    }

    return (
      (!!this.companyFailedBill && !!this.company.defaultPaymentMethodId) ||
      (!!this.organizationFailedBill && !!this.organization?.defaultPaymentMethod)
    );
  }

  public payFailedBill(isOrganization: boolean = false): void {
    if (!this.organizationFailedBill && !this.companyFailedBill) {
      return;
    }

    if (!!this.companyFailedBill && !isOrganization) {
      this.payCompanyMonthlyBill();
    }

    if (!!this.organizationFailedBill && isOrganization) {
      this.organizationsService
        .getOrganizationByIdFromServer(this.currentUser.defaultOrganizationId || this.firstCompany.organizationId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((organization) => {
          this.organization = organization;
          this.payOrganizationMonthlyBill();
        });
    }
  }

  public getOrganizationFailedBill(): void {
    this.getOrganizationMonthlyBills()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: PaginatedResultModel<BillModel>) => {
        this.organizationsService.organizationFailedBill$.next(
          (result?.items || []).find((item: BillModel) => [enumPaymentState.FAILED].includes(item.state) && !item.company),
        );
      });
  }

  public getCompanyFailedBill(): void {
    this.companiesService
      .getCompanyMonthlyBills()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: PaginatedResultModel<BillModel>) => {
        this.companiesService.companyFailedBill$.next(
          (result?.items || []).find((item: BillModel) => [enumPaymentState.FAILED].includes(item.state) && !!item.company),
        );
      });
  }

  private payOrganizationMonthlyBill() {
    switch (this.organization?.defaultPaymentMethod?.provider) {
      case enumPaymentProvider.STRIPE:
        this.organizationsService
          .organizationPayBill(this.organizationFailedBill.id)
          .pipe(
            switchMap(() => this.getOrganizationMonthlyBills()),
            takeUntil(this.destroy$),
          )
          .subscribe((result: PaginatedResultModel<BillModel>) => {
            this.organizationsService.organizationFailedBill$.next(
              (result?.items || []).find((item: BillModel) => [enumPaymentState.FAILED].includes(item.state)),
            );
          });
        break;
      case enumPaymentProvider.PAYPAL:
        this.paymentsRepository
          .create(this.organizationFailedBill.id, true)
          .pipe(
            switchMap(() => this.getOrganizationMonthlyBills()),
            takeUntil(this.destroy$),
          )
          .subscribe((result) => {
            this.organizationsService.organizationFailedBill$.next(
              (result?.items || []).find((item: BillModel) => [enumPaymentState.FAILED].includes(item.state)),
            );
          });
        break;
      default:
        break;
    }
  }

  private payCompanyMonthlyBill() {
    switch (this.currentCompanyPaymentMethod?.provider) {
      case enumPaymentProvider.STRIPE:
        this.companiesService
          .companyPayBill(this.companyFailedBill.id, enumCompanyPaymentType.MONTHLY)
          .pipe(takeUntil(this.destroy$))
          .subscribe((result: PaginatedResultModel<BillModel>) => {
            this.companiesService.companyFailedBill$.next(
              (result?.items || []).find((item: BillModel) => [enumPaymentState.FAILED].includes(item.state)),
            );
          });
        break;
      case enumPaymentProvider.PAYPAL:
        this.paymentsRepository
          .create(this.companyFailedBill.id, true)
          .pipe(
            switchMap(() => this.getCompanyMonthlyBills()),
            takeUntil(this.destroy$),
          )
          .subscribe((result) => {
            this.companiesService.companyFailedBill$.next(
              (result?.items || []).find((item: BillModel) => [enumPaymentState.FAILED].includes(item.state)),
            );
          });
        break;
      default:
        break;
    }
  }

  private calcClasses(): void {
    this.isShowBatchReview = this.calcIsShowBatchReview;
    this.isInLclQuote = this.calcIsInLclQuote;
    this.isInLclBooking = this.calcIsInLclBooking;
    this.isInLclShipment = this.calcIsInLclShipment;
    this.isShowMonthlyFailed = this.calcIsShowMonthlyFailed;
  }

  private getOrganizationMonthlyBills(): Observable<PaginatedResultModel<BillModel>> {
    if (!this.organization?.id) {
      return of(null);
    }
    return this.billsRepository.searchAll(
      {
        organizationId: this.organization.id,
        type: enumCompanyPaymentType.MONTHLY,
      },
      true,
    );
  }

  private getCompanyMonthlyBills(): Observable<PaginatedResultModel<BillModel>> {
    if (!this.company?.id) {
      return of(null);
    }

    return this.billsRepository.search(this.company.id, { type: enumCompanyPaymentType.MONTHLY, companyId: this.company.id }, true);
  }

  private get currentCompanyPaymentMethod(): PaymentMethodModel {
    return (this.paymentMethods || []).find((item: PaymentMethodModel) => item.id === this.company.defaultPaymentMethodId);
  }

  private loadPaymentMethods(): void {
    this.billsRepository
      .paymentMethods(this.company?.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((paymentMethods: PaymentMethodModel[]) => {
        const skipMethods: number[] = [5 /*alipay*/];
        this.paymentMethods = paymentMethods.filter(
          (item: PaymentMethodModel) =>
            (item.provider === enumPaymentProvider.STRIPE ||
              (this.isHasPayPalFastCheckout && item.provider === enumPaymentProvider.PAYPAL)) &&
            !skipMethods.includes(item.codeId),
        ); // atm only stripe & paypal
      });
  }

  private get isHasPayPalFastCheckout(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.paypalFastCheckout);
  }
}
